import { supabase } from './supabase';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /** Correlaciona a tela com o log do Worker. Sem isso, investigar uma falha
     *  do provedor exigia `wrangler tail` e um pedido de "clica de novo". */
    public requestId?: string
  ) {
    super(message);
  }
}

function readActiveWorkspaceId(): string | null {
  try {
    const raw = localStorage.getItem('zaptor:active_workspace_id');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { workspace_id?: string | null } };
    return parsed.state?.workspace_id ?? null;
  } catch {
    return null;
  }
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit & { workspaceRequired?: boolean } = {}
): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  const headers = new Headers(init.headers ?? {});
  if (!headers.has('content-type') && init.body && !(init.body instanceof FormData)) {
    headers.set('content-type', 'application/json');
  }
  if (token) headers.set('authorization', `Bearer ${token}`);

  if (init.workspaceRequired) {
    const ws = readActiveWorkspaceId();
    if (!ws) throw new ApiError(400, 'no_active_workspace', 'no active workspace selected');
    headers.set('x-workspace-id', ws);
  }

  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: { code: string; message: string; request_id?: string };
    };
    if (res.status === 401) {
      await supabase.auth.signOut();
    }
    // `||` e não `??`: quando a resposta não é o JSON de erro do worker (a SPA
    // devolveu index.html e o res.json() falhou, por exemplo), message vem
    // undefined e statusText vem STRING VAZIA — em HTTP/2 e HTTP/3 o statusText
    // não existe no protocolo, e é o que o navegador fala com o Cloudflare. Com
    // `??` a tela renderizava "Erro ao carregar links:" e mais nada, escondendo
    // justamente o caso em que a requisição nem chegou na rota.
    const message = body.error?.message || res.statusText || `HTTP ${res.status}`;
    throw new ApiError(res.status, body.error?.code || 'unknown', message, body.error?.request_id);
  }
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }
  return (await res.json()) as T;
}
